import { useMemo } from "react";

import { BxpWebStargateClientProvider } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";

import { CloudRoadmapSubscriptionForm } from "../../roadmap-subscribtion-form/src/cloud-roadmap-subscription-form.tsx";
import { DataCenterRoadmapSubscriptionForm } from "../../roadmap-subscribtion-form/src/data-center-roadmap-subscription-form.tsx";

import { RoadmapFeaturesQueryResult, RoadmapName } from "./client/client";
import { useQuery } from "./client/hook.ts";
import { Cards } from "./components/cards.tsx";
import { ColumnHeaders } from "./components/column-headers.tsx";
import { Layout } from "./components/layout.tsx";
import { RoadmapSubNav } from "./components/roadmap-subnav.tsx";
import { SearchWithFilters } from "./components/search-with-filters/search-with-filters";
import {
    RoadmapFeaturesContextProvider,
    CLOUD_BASE_PATH,
    DATACENTER_BASE_PATH,
    emptyFilters
} from "./context/context.tsx";
import { getQueryParamFromWindow, getSlugFromWindow } from "./context/url";
import {
    RoadmapFeatureFilters,
    SortCriteria,
    SelectedRoadmapFeature,
    SortKey,
    SortDirection,
    FilterOptionsByKey
} from "./types";

export interface UniformRoadmapFeaturesProps {
    roadmapFeaturesLocale: string;
    roadmapName: RoadmapName;
    data: RoadmapFeaturesQueryResult;
    lastUpdated: string; // ISO 8601 YYYY-MM-DD
}

const UniformRoadmapFeatures = (props: UniformRoadmapFeaturesProps) => {
    const {
        data: ssrData,
        roadmapFeaturesLocale,
        roadmapName,
        lastUpdated
    } = props;

    const pageBasePath =
        roadmapName === "Data Center" ? DATACENTER_BASE_PATH : CLOUD_BASE_PATH;

    const { filters, sortCriteria, selectedFeature } = useMemo(() => {
        if (typeof window === "undefined") {
            return {
                filters: emptyFilters,
                sortCriteria: undefined,
                selectedFeature: undefined
            };
        }

        const filters: RoadmapFeatureFilters = {
            category: getQueryParamFromWindow("category"),
            search: getQueryParamFromWindow("search"),
            status: getQueryParamFromWindow("status"),
            selectedProduct: getQueryParamFromWindow("selectedProduct")
        };

        const sortCriteria: SortCriteria = {
            key: getQueryParamFromWindow("sortBy")[0] as SortKey,
            direction: getQueryParamFromWindow("sort")[0] as SortDirection
        };

        let selectedFeature: SelectedRoadmapFeature | undefined;
        const slug = getSlugFromWindow({ basePath: pageBasePath });
        if (slug) {
            selectedFeature = {
                pid: getQueryParamFromWindow("p")?.[0],
                slug
            };
        }

        return {
            filters,
            sortCriteria,
            selectedFeature
        };
    }, []);

    const { result: csrData } = useQuery({
        locale: roadmapFeaturesLocale,
        roadmapName
    });

    const features =
        csrData?.roadmapFeatureCollection?.items ??
        ssrData?.roadmapFeatureCollection?.items;
    const filterOptionsByKey: FilterOptionsByKey = {
        category:
            ssrData?.taxonomyRoadmapCategoryCollection.items.map((item) => ({
                value: item.queryParameter,
                label: item.roadmapCategoryNameExternal
            })) ?? [],
        selectedProduct: (() => {
            if (!ssrData?.taxonomyRoadmapAppCollection.items) {
                return [];
            }

            // Group apps by filterQueryParam and keep only the shortest label for each
            const groupedByQueryParam = new Map<string, string>();

            for (const item of ssrData.taxonomyRoadmapAppCollection.items) {
                const queryParam = item.filterQueryParam;
                const currentName = item.roadmapAppNameExternal;
                const existing = groupedByQueryParam.get(queryParam);

                if (!existing || currentName.length < existing.length) {
                    groupedByQueryParam.set(queryParam, currentName);
                }
            }

            return Array.from(groupedByQueryParam.entries()).map(
                ([value, label]) => ({ value, label })
            );
        })(),
        status:
            ssrData?.taxonomyRoadmapStatusCollection.items.map((item) => ({
                value: item.roadmapStatusNameExternal,
                label: item.roadmapStatusNameExternal
            })) ?? []
    };

    return (
        <BxpWebStargateClientProvider
            baseUrl={process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"] as string}
        >
            <RoadmapFeaturesContextProvider
                pageBasePath={pageBasePath}
                locale={roadmapFeaturesLocale}
                filters={filters}
                sortCriteria={sortCriteria}
                selectedFeature={selectedFeature}
                features={features}
                filterOptionsByKey={filterOptionsByKey}
            >
                {/* this div is necessary to prevent unnwanted spacing because the parent container 
                    is a column layout which has inherent gap spacing */}
                <div>
                    <RoadmapSubNav />
                    {roadmapName === "Data Center" ? (
                        <DataCenterRoadmapSubscriptionForm
                            lastUpdated={lastUpdated}
                        />
                    ) : (
                        <CloudRoadmapSubscriptionForm
                            lastUpdated={lastUpdated}
                        />
                    )}
                    <SearchWithFilters />
                    <Layout
                        columnHeaders={<ColumnHeaders />}
                        cards={<Cards />}
                    />
                </div>
            </RoadmapFeaturesContextProvider>
        </BxpWebStargateClientProvider>
    );
};

export default UniformRoadmapFeatures;
